import React, { useState, useEffect, Fragment, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  NavLink,
  useRouteMatch,
  Route,
  Switch,
  useParams,
  useHistory,
  useLocation,
} from "react-router-dom";

import {
  createJourney,
  deleteJourney,
  fetchJourney,
  fetchJourneys,
  updateJourney,
  updateJourneyData,
} from "actions/journey";
import { fetchCustomFields } from "actions/fields/fields";
import { createMixpanelEvent } from "actions/mixpanel";
import { JOURNEY_ENABLED, MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_JOURNEY } from "constants/mixpanelEvents";
import { getPageViewedEventPayload } from 'services/mixpanelUtil';
import cx from "classnames";
import { ReactFlowProvider, useStoreApi } from "@xyflow/react";
import _, { isEqual } from "lodash";
import dateformat from "dateformat";
import { Property } from "components/Editor/components/properties";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import ChartEditor from "./components/chartEditor";
import { toast } from "react-toastify";
import { SuccessToast } from "components/Toast";
import {
  Row,
  Col,
  Button,
  Image,
  Offcanvas,
  ListGroup,
  Modal,
  Card,
  Dropdown,
  OverlayTrigger,
  Popover,
  Placeholder,
} from "react-bootstrap";
import "@xyflow/react/dist/style.css";
import InfiniteScroll from "components/InfiniteScroll";
import Tooltip from "components/Tooltip";
import storyboardRoleService from "services/storyboardRoles.service";

import styles from "./journey.module.scss";

const JourneyContainer = () => {
  const dispatch = useDispatch();
  let { storyboardId } = useParams();
  let { path } = useRouteMatch();
  const location = useLocation();
  let { url } = useRouteMatch();
  const history = useHistory();

  const loading = useSelector((state) =>
    _.get(state, "journey.loading", false)
  );
  const creating = useSelector((state) =>
    _.get(state, "journey.creating", false)
  );
  const journeyList = useSelector((state) =>
    _.get(state, "journey.items", false)
  );
  const storyboard = useSelector(state => 
    _.get(state, 'storyboards.storyboard.item', null)
  );
  const pagination = useSelector(state => _.get(state, 'journey.pagination', {}));
  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  useEffect(() => {
    const currentUrl = location?.pathname;
    const journeyIndex = currentUrl.indexOf("/journey/");
    const firstJourney = journeyList[0];
    
    if (journeyIndex == -1 && firstJourney) {
      history.push(`${url}/${firstJourney.id}`);
    }
  }, [location]);

  useEffect(() => {
    if (storyboardId) {
      handleFetchJourneys(0)
      dispatch(fetchCustomFields(storyboardId));
    }
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_JOURNEY, storyboardId, storyboard.name, storyboard.type)
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
  }, []);

  const loadMoreJourneys = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchJourneys(nextPage);
  }

  const handleFetchJourneys = (page) => {

    if (storyboardId) {
      var request = {
        storyboard: storyboardId,
        page: page,
        limit: 10,
        sort: 'createdDate',
        order: 'desc'
      }
      dispatch(fetchJourneys(request));
    }
  }

  const handleCreateJourney = () => {
    const request = {
      name: `untitled-${_.size(journeyList) + 1}`,
      storyboard: storyboardId,
    };
    dispatch(createJourney(request));
  };

  const handleFetchJourney = (journeyId) => {
    if (journeyId) {
      dispatch(fetchJourney(journeyId));
    }
  };

  return (
    <Fragment>
      <section className={styles.content}>
        <header className={styles.header}>
          <div>
            <div className={styles.title}>Journey's</div>
            <div className={styles.description}>
              Create guided content paths for increased engagement
            </div>
          </div>
          {!loading && _.size(journeyList) > 0 && (
            <Button
              className={styles.addButton}
              variant="primary"
              disabled={creating || !storyboardRoleService.canEditStoryboard(storyboard)}
              onClick={handleCreateJourney}
            >
              <i className={cx(styles.icon, "cleverstory-plus")} />
              New Journey
            </Button>
          )}
        </header>

        <section className={cx(styles.tabContainer, {[styles.disabled] : !storyboardRoleService.canEditStoryboard(storyboard)})}>
          {loading && _.size(journeyList) < 1 && <LoadingMessage />}
          {!loading && _.size(journeyList) < 1 && (
            <JourneyEmptyMessage handleCreate={handleCreateJourney} />
          )}
          {_.size(journeyList) > 0 && (
            <div className={styles.tabSection}>
              <section className={cx(styles.sideMenu)}>
                <SideMenuConatiner
                  handleCreateJourney={handleCreateJourney}
                  journeyList={journeyList}
                  loadMoreJourneys={loadMoreJourneys}
                  pagination={pagination}
                  hasMoreItems={hasMoreItems}
                />
              </section>
              <section className={styles.editorSection}>
                <Switch>
                  <Route path={`${path}/:journeyId`}>
                    <ReactFlowProvider>
                      <JourneyChartEditor
                        handleFetchJourney={handleFetchJourney}
                      />
                    </ReactFlowProvider>
                  </Route>
                </Switch>
              </section>
            </div>
          )}
        </section>
      </section>
    </Fragment>
  );
};

export const JourneyChartEditor = ({ handleFetchJourney }) => {
  const dispatch = useDispatch();
  let { journeyId } = useParams();
  let { storyboardId } = useParams();
  const inputRef = React.useRef(null);
  const prevStepActionRef = React.useRef({ creating: false, updating: false });
  const store = useStoreApi();
  const journey = useSelector((state) => _.get(state, `journey`, {}));
  const journeyDetails = _.get(journey, `${journeyId}`, {});
  const journeySteps =
    useSelector((state) => _.get(state, `journey.${journeyId}.steps`, [])) ||
    [];
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const journeyitems = _.get(journey, "items", []) || [];
  const loading = _.get(journeyDetails, "loading", false);
  const [inputName, setInputName] = useState(journeyDetails.name);
  const [editable, setEditable] = useState(false);
  const [deleteElement, setDeleteElement] = useState(null);
  const updating = _.get(journeyDetails, "updating", false);

  const deletion = useSelector((state) =>
    _.get(state, "journey.deletion", null)
  );
  const deleting = _.get(deletion, "deleting", null);
  const deleted = _.get(deletion, "deleted", null);

  useEffect(() => {
    if (deleted) {
      setDeleteElement(null);
    }
  }, [deleted]);

  const isStepOperationInProgress = journeySteps.some(
    step => step.loading === true || step.updating === true
  );

  useEffect(() => {
    const wasInProgress = prevStepActionRef.current.creating || prevStepActionRef.current.updating;
    const justCompleted = wasInProgress && !isStepOperationInProgress;

    if (justCompleted && !updating && !loading && journeyId) {
      handleSave(false);
    }

    prevStepActionRef.current = {
      creating: isStepOperationInProgress,
      updating: isStepOperationInProgress
    };
  }, [isStepOperationInProgress, updating, loading, journeyId]);

  const handleDelete = () => {
    if (journeyDetails?.id) {
      dispatch(deleteJourney(journeyDetails?.id));
    }
  };

  const handleSave = useCallback((shouldReset = true) => {
    const { nodes, edges } = store.getState();
    const startNode = journeySteps.find((data) => data.start);
    const steps = _.map(nodes, (node) => {
      const stepData = journeySteps.find(
        (data) => data.uid === node.id || data.id === node.id || data.stepUid === node.id
      );
      if (stepData && stepData.id) {
        return stepData.id;
      }
    });

    const connectors = _.map(edges, (edge) => {
      const startNode = journeySteps.find(
        (data) => data.uid === edge.source || data.id === edge.source || data.stepUid === edge.source
      );
      const endNode = journeySteps.find(
        (data) => data.uid === edge.target || data.id === edge.target || data.stepUid === edge.target
      );
      const commands = _.get(edge, "data.commands", []) || [];

      return {
        start: startNode?.id,
        end: endNode?.id,
        commands: commands,
      };
    });

    const request = {
      name: _.get(journeyDetails, "name"),
      disabled: _.get(journeyDetails, "disabled"),
      steps: _.compact(steps),
      start: startNode?.id,
      connectors: connectors,
      storyboard: storyboardId,
    };

    const updatingJourney = journeyitems.find((journey) => journey.id === journeyId);
    if (updatingJourney && !updatingJourney.disabled && request.disabled) {
      triggerJourneyEnabled(request, journeyId);
    }

    dispatch(updateJourney(journeyId, request, shouldReset ? handleReset : undefined));
  }, [store, journeySteps, journeyDetails, journeyId, journeyitems, storyboardId]);

  const handleReset = () => {
    if (journeyId) {
      dispatch(fetchJourney(journeyId, false));
    }
  };

  const triggerJourneyEnabled = (request, journeyId) => {
    const mixpanelPayload = {
      storyboard_id: storyboard.id,
      storyboard_name: storyboard.name,
      journey_id: journeyId,
      journey_name: request.name,
    };
    dispatch(createMixpanelEvent(JOURNEY_ENABLED, mixpanelPayload));
  };

  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const handleNameChange = (e) => {
    setInputName(e.target.value);
  };

  const updateJourneyValue = (data, key) => {
    const request = {
      [key]: data,
    };
    dispatch(updateJourneyData(journeyId, request));
    setEditable(false);
  };

  useEffect(() => {
    setInputName(journeyDetails?.name);
  }, [journeyDetails]);

  useEffect(() => {
    if (journeyId) {
      handleFetchJourney(journeyId);
    }
  }, [journeyId]);

  return (
    <>
      {loading && <LoadingMessage />}
      {!loading && journeyDetails && (
        <>
          <div className={styles.header}>
            {!editable && (
              <div className={styles.nameAction}>
                <span className={styles.title}>
                  <TruncatedTextWithTooltip
                    content={_.get(journeyDetails, "name", "")}
                    maxWidth={120}
                    placement="top"
                  />
                </span>
                <Button
                  variant="info"
                  className={styles.iconButton}
                  onClick={() => setEditable(true)}
                >
                  <i className={cx(styles.icon, "cleverstory-edit")} />
                </Button>
              </div>
            )}

            {editable && (
              <input
                ref={inputRef}
                type={"text"}
                className={styles.inputFieldName}
                value={inputName}
                onChange={handleNameChange}
                onBlur={() => updateJourneyValue(inputName, "name")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    updateJourneyValue(inputName, "name");
                  }
                }}
              />
            )}
            <Button
              variant="info"
              onClick={() => {
                setDeleteElement(journeyDetails?.id);
              }}
              className={styles.iconButton}
            >
              <i className={cx(styles.icon, "cleverstory-trash")} />
            </Button>
          </div>
          <div className={cx(styles.chartRender, {[styles.disabled] : !storyboardRoleService.canEditStoryboard(storyboard)})}>
            <ChartEditor />
          </div>
          {storyboardRoleService.canEditStoryboard(storyboard) &&
            <div className={styles.footer}>
              <Button
                variant="secondary"
                className={styles.cancelButton}
                onClick={handleReset}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={updating}
                className={styles.cancelButton}
              >
                {updating ? "Saving..." : `Save Changes`}
              </Button>
            </div>
          }
        </>
      )}
      <DeleteJourneyModal
        show={deleteElement}
        deleting={deleting}
        onDelete={handleDelete}
        onCancel={() => {
          setDeleteElement(null);
        }}
      />
    </>
  );
};

const SideMenuConatiner = React.memo(
  ({ creatingJourney, handleCreateJourney, journeyList, pagination, hasMoreItems, loadMoreJourneys }) => {
    let { url } = useRouteMatch();
    const history = useHistory();
    const [journeyCount, setJourneyCount]=useState(_.size(journeyList));

    const loading = useSelector(state => _.get(state, 'storyboards.loading', false));

    useEffect(() => {
      if (!_.isEqual(journeyCount, _.size(journeyList))) {
        setJourneyCount(_.size(journeyList))
        routeJourneyDetails()
      }
    }, [journeyList]);

    useEffect(()=>{
      routeJourneyDetails();
    },[])

    const routeJourneyDetails =()=>{
      const firstJourney = journeyList[0];
      history.push(`${url}/${firstJourney.id}`);
    }

    return (
      <div className={styles.detailsContainer}>
        <div className={styles.header}>
          <span className={styles.title}>Your Journey</span>
          <Button
            onClick={handleCreateJourney}
            variant="info"
            className={styles.addAction}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
          </Button>
        </div>
          <InfiniteScroll
            className={styles.list}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={() => {
              loadMoreJourneys();
            }}
          >
            {_.map(journeyList, (journey) => {
              return <JourneyDetailView journey={journey} />;
            })}
          </InfiniteScroll>
      </div>
    );
  }
);

const JourneyDetailView = React.memo(({ journey }) => {
  const dispatch = useDispatch();
  let { url } = useRouteMatch();
  const journeyUrl = window.location.href;
  const parts = journeyUrl.split("/");
  const journeyId = parts[parts.length - 1];
  const triggerAssetName = _.get(journey, "trigger");

  const [enable, setEnable] = useState(journey?.disabled);
  const deletion = useSelector((state) =>
    _.get(state, "journey.deletion", null)
  );
  const deleting = _.get(deletion, "deleting", null);
  const deletingId = _.get(deletion, "id", null);

  useEffect(() => {
    if (enable != journey?.disabled) {
      setEnable(journey?.disabled);
    }
  }, [journey]);

  const updateJourneyValue = (data, key) => {
    const request = {
      [key]: data,
    };
    if (journeyId == journey.id) {
      dispatch(updateJourneyData(journey.id, request));
      setEnable(!enable);
    }
  };

  return (
    <>
      <ListGroup.Item
        as={NavLink}
        key={journey.id}
        id={journey.id}
        to={`${url}/${journey.id}`}
        className={styles.journeyDetail}
      >
        <div className={styles.headerActions}>
          <span className={styles.name}>
            <TruncatedTextWithTooltip
              content={journey.name}
              maxWidth={120}
              placement="top"
            />
          </span>

          <div
            className={styles.actionContainer}
            style={{ opacity: journeyId == journey.id ? 1 : 0.5 }}
          >
            <Property.Switch
              id={journey?.id}
              title={""}
              value={enable}
              onChange={(event) => {
                event.preventDefault();
                event.stopPropagation();
                updateJourneyValue(!enable, "disabled");
              }}
            />
          </div>
        </div>
        <span className={styles.updateDate}>
          Updated on: {dateformat(journey.modifiedDate, "dd mmm yyyy")}
        </span>
        <div className={styles.triggerDetails}>
          {triggerAssetName && (
            <>
              <span className={styles.tag}>Trigger:</span>
              <Tooltip
                placement="top" 
                content={<div dangerouslySetInnerHTML={{ __html: triggerAssetName }} className={styles.tooltipFilterContent}/>} 
                className={styles.tooltip}>
                <div dangerouslySetInnerHTML={{ __html: triggerAssetName }} className={styles.filterContent}/>
              </Tooltip>
            </>
          )}
        </div>
        {deleting && deletingId === journey.id && (
          <div className={styles.overlay}>
            <span className={styles.deleting}>Deleting...</span>
          </div>
        )}
      </ListGroup.Item>
    </>
  );
});

const JourneyEmptyMessage = ({ handleCreate }) => {
  return (
    <div className={styles.emptyFieldsMessageContainer}>
      <div className={styles.emptyMessage}>
        <div className={styles.heading}>Weather forecast</div>
        <div className={styles.description}>
          Clear Skies and Empty <br /> Roads
        </div>
        <Button
          onClick={handleCreate}
          className={styles.addButton}
          variant="primary"
          type="submit"
        >
          <i className={cx(styles.icon, "cleverstory-plus")} />
          New Journey
        </Button>
      </div>
    </div>
  );
};

const LoadingMessage = () => {
  return (
    <div className={styles.loaderContainer}>
      <label>Just a moon-ute... Scanning for routes...</label>
    </div>
  );
};

const DeleteJourneyModal = ({ show, deleting, onDelete, onCancel }) => {
  return (
    <Modal
      backdrop="static"
      className={styles.deleteConfirmModal}
      contentClassName={styles.deleteConfirmContent}
      dialogClassName={styles.deleteConfirmDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered
      size="lg"
    >
      <Modal.Body className={styles.deleteConfirmBody}>
        <Image
          src={"/images/deleteConfirm.png"}
          className={styles.deleteConfirmImage}
        />
        <Row className={styles.messageRow}>
          <Col className={styles.messageContainer} xs={{ span: 6, offset: 6 }}>
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <span className={styles.messageContent}>
              This action of yours will delete the current journey.
            </span>
            <span className={styles.messageContent}>
              Sure you want to continue?
            </span>
            <footer className={styles.deleteConfirmButtons}>
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" disabled={deleting} onClick={onDelete}>
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </footer>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default JourneyContainer;
